import { motion } from 'framer-motion'
import { Target } from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { formatCurrencyCompact, formatCurrency } from '@/utils/format-currency'
import { GoalEditor } from './GoalEditor'

interface BarChartSectionProps {
  period: 'monthly' | 'quarterly' | 'annual'
  periodLabel: string
  barData: { name: string; key: string; realizado: number; meta: number; expectation: number }[]
  attainment: Record<string, number>
  chartTitle?: string
}

function BarTooltip({ active, payload, label }: any) {
  if (!active || !payload) return null
  return (
    <div className="glass-card p-3 space-y-1" style={{ padding: 12 }}>
      <p className="text-text-primary font-semibold text-sm">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} className="text-xs" style={{ color: p.color }}>
          {p.name}: {formatCurrency(p.value)}
        </p>
      ))}
    </div>
  )
}

export function BarChartSection({
  period,
  periodLabel,
  barData,
  attainment,
  chartTitle = 'Realizado vs Meta por Categoria',
}: BarChartSectionProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.45, duration: 0.35 }}
      className="glass-card space-y-4"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-text-primary font-semibold text-sm flex items-center gap-2">
          <Target size={14} className="text-accent" />
          {chartTitle}
        </h3>
        <span className="text-text-muted text-xs">{periodLabel}</span>
      </div>

      <GoalEditor period={period} attainment={attainment} />

      <div className='h-64 min-h-64 min-w-0 w-full'>
        <ResponsiveContainer width='100%' height='100%' minWidth={0}>
          <BarChart data={barData} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis
              dataKey="name"
              tick={{ fill: 'var(--text-muted)', fontSize: 12 }}
              axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: 'var(--text-muted)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => formatCurrencyCompact(v)}
            />
            <Tooltip content={<BarTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, color: 'var(--text-muted)' }} />
            <Bar
              dataKey="realizado"
              name="Realizado"
              fill="var(--accent, #6366f1)"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="meta"
              name="Meta"
              fill="rgba(255,255,255,0.15)"
              radius={[4, 4, 0, 0]}
            />
            <Bar
              dataKey="expectation"
              name="Expectativa"
              fill="rgba(16,185,129,0.5)"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  )
}
